// Conclusão de visita a partir de uma TAREFA do tipo visita (doc Tarefas — Correção).
// Reaproveita o mesmo painel "Concluir Visita" das Rotas: concluir a tarefa registra o
// resultado da visita no lead e, se houver, agenda automaticamente a próxima atividade.
import { supabase } from './supabase';
import { statusVisitaLabel, proximaAcaoLabel, VisitaConclusao } from '../../views/CRM/Rotas/VisitaModal';
import { PosVendaResultado, POS_VENDA_RESULTADO_LABELS } from '../../types';

interface TarefaVisita {
    id: string | number;
    titulo?: string;
    responsavel_id?: string | null;
    negociacao_id?: number | null;
    negociacao?: { titulo?: string | null } | null;
}

/** Texto do resultado da conclusão (status escolhido + observação) — visível no card da agenda. */
export const textoResultado = (dados: VisitaConclusao): string =>
    [dados.statusVisita ? statusVisitaLabel(dados.statusVisita) : '', (dados.descricao || '').trim()]
        .filter(Boolean)
        .join(' — ');

/** Conclui a tarefa gravando o resultado na descrição, que aparece no card da agenda
 * (pedido Bruno 08/jul: a observação da conclusão sumia nas visitas da Prospecção Geo).
 * Se a tarefa já tem descrição (digitada na criação), o resultado é anexado abaixo. */
export const concluirTarefaComResultado = async (
    tarefaId: string | number,
    resultado?: string | null,
    resultadoCategoria?: PosVendaResultado[] | null,
): Promise<string> => {
    const agora = new Date().toISOString();
    const patch: {
        status: string; data_conclusao: string; descricao?: string; resultado_categoria?: string[];
    } = { status: 'concluido', data_conclusao: agora };
    // Chips de pós-venda: só grava quando vierem preenchidos, para não zerar o que já existe
    // numa reabertura/conclusão vinda de outro ponto do sistema.
    if (resultadoCategoria?.length) patch.resultado_categoria = resultadoCategoria;
    const res = (resultado || '').trim();
    if (res) {
        const { data } = await supabase.from('tarefas').select('descricao').eq('id', tarefaId).maybeSingle();
        const atual = (data?.descricao || '').trim();
        patch.descricao = atual ? `${atual}\n${res}` : res;
    }
    const { error } = await supabase.from('tarefas').update(patch).eq('id', tarefaId);
    if (error) throw error;
    return agora;
};

/** Conclui a tarefa de visita, registra no lead e cria a próxima atividade quando agendada. */
export const concluirVisitaTarefa = async (tarefa: TarefaVisita, dados: VisitaConclusao, userId?: string) => {
    // 1) Conclui a tarefa (Status → Concluído), gravando o resultado na descrição.
    const agora = await concluirTarefaComResultado(tarefa.id, textoResultado(dados), dados.resultadoCategoria);

    if (tarefa.negociacao_id) {
        // 2) Registra o resultado da visita no histórico do lead + conta como interação.
        const partes = [dados.statusVisita ? `📋 ${statusVisitaLabel(dados.statusVisita)}` : '✅ Visita realizada', `(${new Date().toLocaleDateString('pt-BR')})`];
        if (dados.descricao) partes.push(`— ${dados.descricao}`);
        if (dados.resultadoCategoria?.length) {
            partes.push(`· ${dados.resultadoCategoria.map(r => POS_VENDA_RESULTADO_LABELS[r] || r).join(', ')}`);
        }
        if (dados.proximaAcao) partes.push(`· Próxima ação: ${proximaAcaoLabel(dados.proximaAcao)}`);
        if (userId) {
            await supabase.from('negociacao_notas').insert([{
                negociacao_id: tarefa.negociacao_id,
                user_id: userId,
                conteudo: partes.join(' '),
                tipo: 'nota',
            }]);
        }
        await supabase.from('negociacoes')
            .update({ ultima_atividade_at: agora, updated_at: agora })
            .eq('id', tarefa.negociacao_id);

        // 3) Loop "concluir → planejar próxima": cria a próxima visita/ligação na agenda.
        if (dados.proximaVisitaData) {
            await supabase.from('tarefas').insert([{
                titulo: `Visita: ${tarefa.negociacao?.titulo || tarefa.titulo || 'lead'}`,
                responsavel_id: tarefa.responsavel_id || userId,
                solicitante_id: userId,
                prioridade: 'media',
                vencimento: dados.proximaVisitaData,
                status: 'a_iniciar',
                negociacao_id: tarefa.negociacao_id,
                tipo: 'visita',
                modalidade: dados.proximaVisitaModalidade || 'presencial',
                created_at: agora,
            }]);
        }
    }
};

// ── Gestão de tarefa comum (não-visita), a partir do feed do Lead ─────────────
// Espelha o fluxo "Concluir Visita" para tarefas comuns, mas sem sair do Kanban.

/** Registra uma nota no histórico do lead + conta como interação. */
const registrarNotaLead = async (negociacaoId: number, conteudo: string, userId?: string) => {
    const agora = new Date().toISOString();
    if (userId) {
        await supabase.from('negociacao_notas').insert([{
            negociacao_id: negociacaoId, user_id: userId, conteudo, tipo: 'nota',
        }]);
    }
    await supabase.from('negociacoes')
        .update({ ultima_atividade_at: agora, updated_at: agora })
        .eq('id', negociacaoId);
};

/** Conclui uma tarefa comum, registra no lead e (opcional) agenda um retorno. */
export const concluirTarefa = async (tarefa: TarefaVisita, dados: VisitaConclusao, userId?: string) => {
    const agora = await concluirTarefaComResultado(tarefa.id, textoResultado(dados));

    if (tarefa.negociacao_id) {
        const partes = [`✅ Tarefa concluída${tarefa.titulo ? `: ${tarefa.titulo}` : ''}`, `(${new Date().toLocaleDateString('pt-BR')})`];
        if (dados.descricao) partes.push(`— ${dados.descricao}`);
        await registrarNotaLead(tarefa.negociacao_id, partes.join(' '), userId);

        // Loop "concluir → planejar próxima": cria o retorno na agenda.
        if (dados.proximaVisitaData) {
            await supabase.from('tarefas').insert([{
                titulo: `Retorno: ${tarefa.negociacao?.titulo || tarefa.titulo || 'lead'}`,
                responsavel_id: tarefa.responsavel_id || userId,
                solicitante_id: userId,
                prioridade: 'media',
                vencimento: dados.proximaVisitaData,
                status: 'a_iniciar',
                negociacao_id: tarefa.negociacao_id,
                modalidade: dados.proximaVisitaModalidade || 'presencial',
                created_at: agora,
            }]);
        }
    }
};

/** Reagenda a tarefa (muda só a data planejada) e registra no lead. */
export const reagendarTarefa = async (
    tarefa: TarefaVisita, novaData: string, userId?: string, dataOriginal?: string,
) => {
    const { error } = await supabase
        .from('tarefas')
        .update({ vencimento: novaData, status: 'a_iniciar' })
        .eq('id', tarefa.id);
    if (error) throw error;

    if (tarefa.negociacao_id) {
        const fmt = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');
        const conteudo = dataOriginal
            ? `📅 Tarefa reagendada: ${fmt(dataOriginal)} → ${fmt(novaData)}`
            : `📅 Tarefa reagendada para ${fmt(novaData)}`;
        await registrarNotaLead(tarefa.negociacao_id, conteudo, userId);
    }
};

/** Cancela a tarefa e registra o motivo no lead. */
export const cancelarTarefa = async (tarefa: TarefaVisita, motivo: string, userId?: string) => {
    const { error } = await supabase
        .from('tarefas')
        .update({ status: 'cancelado' })
        .eq('id', tarefa.id);
    if (error) throw error;

    if (tarefa.negociacao_id) {
        const conteudo = `🗑️ Tarefa cancelada${tarefa.titulo ? `: ${tarefa.titulo}` : ''}${motivo ? ` — ${motivo}` : ''}`;
        await registrarNotaLead(tarefa.negociacao_id, conteudo, userId);
    }
};
